// File Metadata:
// Version: 1.02
// Created: 2025-05-15
// Last Modified: 2025-05-18
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: Profile slice for managing user profile data using axiosInstance

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../../store/api/axiosInstance';

const initialState = {
  user: null,
  status: 'idle',
  error: null,
};

// Async thunk for fetching user profile data
export const fetchUserProfile = createAsyncThunk(
  'profile/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/profile');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch user profile');
    }
  }
);

// Async thunk for updating user profile data
export const updateUserProfile = createAsyncThunk(
  'profile/updateUserProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('/profile', profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update user profile');
    }
  }
);

// Async thunk for setting up 2FA
export const setupTwoFactorAuth = createAsyncThunk(
  'profile/setupTwoFactorAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/2fa/setup');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to setup two-factor authentication');
    }
  }
);

// Async thunk for verifying 2FA
export const verifyTwoFactorAuth = createAsyncThunk(
  'profile/verifyTwoFactorAuth',
  async (code, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/2fa/verify', { code });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to verify two-factor authentication');
    }
  }
);

// Async thunk for disabling 2FA
export const disableTwoFactorAuth = createAsyncThunk(
  'profile/disableTwoFactorAuth',
  async (code, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/2fa/disable', { code });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to disable two-factor authentication');
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    clearUser(state) {
      state.user = null;
    },
  },  extraReducers: (builder) => {
    builder
      // Handle fetchUserProfile actions
      .addCase(fetchUserProfile.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      
      // Handle updateUserProfile actions
      .addCase(updateUserProfile.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      
      // Handle setupTwoFactorAuth actions
      .addCase(setupTwoFactorAuth.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(setupTwoFactorAuth.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(setupTwoFactorAuth.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      
      // Handle verifyTwoFactorAuth actions
      .addCase(verifyTwoFactorAuth.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyTwoFactorAuth.fulfilled, (state) => {
        state.status = 'succeeded';
        if (state.user) {
          state.user.twoFactorEnabled = true;
        }
      })
      .addCase(verifyTwoFactorAuth.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      
      // Handle disableTwoFactorAuth actions
      .addCase(disableTwoFactorAuth.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(disableTwoFactorAuth.fulfilled, (state) => {
        state.status = 'succeeded';
        if (state.user) {
          state.user.twoFactorEnabled = false;
        }
      })
      .addCase(disableTwoFactorAuth.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      });
  },
});

export const { setUser, clearUser } = profileSlice.actions;
export default profileSlice.reducer;
